import {BaseComponent} from '../base/component.base.js';
import {escapeHtml} from '../../core/escape-html.js';
import {formatDate, formatEuro} from '../../core/billing/billing.core.js';
import {ONE_OFF_PART_NAME, ONE_OFF_ROW, reportRows} from '../../core/billing/report.core.js';
import {BILLING_GROUP} from '../../core/vocabulary.js';
import template from './billing-totals.template.html';
import styles from './billing-totals.styles.css';

const text = escapeHtml;
const money = (value) => `<td class="num${value < 0 ? ' negative' : ''}">${text(formatEuro(value))}</td>`;
const costCell = (value) => value === null ? '<td class="num" title="No separate supplier cost recorded">—</td>' : money(value);
const clientLink = (invoice) => invoice.deleted ? `${text(invoice.clientName)} (deleted)`
    : `<a href="#/clients/${encodeURIComponent(invoice.clientSlug)}">${text(invoice.clientName)}</a>`;

/** One table of recorded money; expanded rows show the entries that make up each sum. */
export class BillingTotalsComponent extends BaseComponent {
    #report;
    #year;
    #group = BILLING_GROUP.SUPPLIER;
    #expanded = new Set();
    #rowKeys = [];

    connectedCallback() {
        this.loadTemplate(template, styles);
        this.$('#all-years').addEventListener('click', () => this.emit('billing-year-select', {year: null}));
        for (const button of this.$$('.group-button')) button.addEventListener('click', () => {
            this.#group = button.dataset.group;
            this.#expanded.clear();
            this.#render();
        });
        this.$('#totals-content').addEventListener('click', (event) => {
            const button = event.target.closest('[data-row]');
            if (button) {
                const index = Number(button.dataset.row);
                const key = this.#rowKeys[index];
                this.#expanded.has(key) ? this.#expanded.delete(key) : this.#expanded.add(key);
                this.#render();
                this.$(`[data-row="${index}"]`).focus({preventScroll: true});
            }
            if (event.target.closest('[data-manage-costs]')) this.emit('billing-manage-costs');
        });
    }

    /** @param {import('../../shell/types/billing.types.js').BillingReportApiResponse} report
     * @param {number|null} year null shows all years */
    showReport(report, year) {
        if (year !== this.#year) this.#expanded.clear();
        this.#report = report;
        this.#year = year;
        this.#render();
    }

    showUnavailable() {
        this.#report = undefined;
        this.$('#totals-content').innerHTML = '<p class="empty">Not available right now.</p>';
    }

    #render() {
        for (const button of this.$$('.group-button')) {
            const active = button.dataset.group === this.#group;
            button.classList.toggle('active', active);
            button.setAttribute('aria-pressed', String(active));
        }
        if (!this.#report) return;
        this.$('#period-title').textContent = this.#year === null ? 'All years' : String(this.#year);
        this.$('#all-years').hidden = this.#year === null;
        const {rows, total, oneOff} = reportRows(this.#report, this.#year, this.#group);
        this.#rowKeys = rows.map((row) => row.key);
        const label = this.#group === BILLING_GROUP.SUPPLIER ? 'Supplier' : 'Client';
        this.$('#totals-content').innerHTML = rows.length ? `<div class="table-wrap"><table class="totals-table" aria-label="${label} results for ${this.#year ?? 'all years'}">
            <colgroup><col class="name-column"><col><col><col></colgroup><thead><tr><th scope="col">${label}</th><th scope="col" class="num">Client income</th><th scope="col" class="num">Costs</th><th scope="col" class="num">Result</th></tr></thead>
            ${rows.map((row, index) => this.#row(row, index)).join('')}
            <tfoot><tr><th scope="row">Total</th>${money(total.income)}${money(total.costs)}${money(total.result)}</tr>${oneOff ? `<tr class="split-row one-off"><th scope="row">${ONE_OFF_PART_NAME}</th>${money(oneOff.income)}${money(oneOff.costs)}${money(oneOff.result)}</tr>` : ''}</tfoot>
        </table></div>` : '<p class="empty">No recorded invoices or costs for this period.</p>';
    }

    #row(row, index) {
        const open = this.#expanded.has(row.key);
        const split = row.split ? [['Recurring', row.split.recurring, ''], ['One-off (not recurring)', row.split.oneOff, ' one-off']]
            .map(([name, part, kind]) => `<tr class="split-row${kind}"><th scope="row">${name}</th>${money(part.income)}${money(part.costs)}${money(part.result)}</tr>`).join('') : '';
        return `<tbody${row.key === ONE_OFF_ROW ? ' class="one-off"' : ''}><tr><th scope="row"><button class="row-button" data-row="${index}" aria-expanded="${open}" aria-controls="detail-${index}"><span aria-hidden="true">${open ? '−' : '+'}</span> ${text(row.name)}${row.deleted ? ' (deleted)' : ''}</button></th>
            ${money(row.income)}${money(row.costs)}${money(row.result)}</tr>${split}
            <tr id="detail-${index}" class="detail-row" ${open ? '' : 'hidden'}><td colspan="4">${this.#details(row)}</td></tr></tbody>`;
    }

    #details(row) {
        const invoices = row.invoices.map((invoice) => `<tr><td>${text(invoice.invoiceNumber)}</td><td>${text(formatDate(invoice.invoicedOn))}</td>
            <td>${clientLink(invoice)}<br><span class="muted">${text(invoice.label)} · ${text(invoice.supplier ?? 'No supplier')}</span></td>
            <td>${text(formatDate(invoice.periodFrom))} – ${text(formatDate(invoice.periodTo))}</td>${money(invoice.amount)}
            ${costCell(invoice.cost)}</tr>`).join('');
        const notes = [...new Set(row.invoices.filter((entry) => entry.itemNotes).map((entry) => `${entry.clientName} · ${entry.label}: ${entry.itemNotes}`))];
        const platform = row.platformCosts.map((cost) => `<tr><td>${text(cost.invoiceNumber ?? 'No invoice number')}</td><td>${text(formatDate(cost.periodTo))}</td><td>${text(cost.supplier)}<br><span class="muted">${text(cost.label)} · Shared platform cost</span>
            ${cost.documentUrl?.startsWith('https://') ? `<br><a href="${text(cost.documentUrl)}" target="_blank" rel="noopener noreferrer">Document</a>` : ''}</td>
            <td>${text(formatDate(cost.periodFrom))} – ${text(formatDate(cost.periodTo))}</td>${money(0)}${money(cost.amount)}</tr>`).join('');
        const oneOff = row.oneOffIncomes.map((income) => `<tr class="one-off"><td>${text(income.invoiceNumber)}</td><td>${text(formatDate(income.invoicedOn))}</td>
            <td>${clientLink(income)}<br><span class="muted">${text(income.label)}</span> <span class="one-off-tag">One-off, not recurring</span></td>
            <td class="muted">No service period</td>${money(income.amount)}${costCell(income.cost)}</tr>`).join('');
        return `<div class="details-wrap"><table class="detail-table" aria-label="Entries for ${text(row.name)}"><thead><tr><th scope="col">Invoice</th><th scope="col">Date</th><th scope="col">Client / item</th><th scope="col">Service period</th><th scope="col" class="num">Client income</th><th scope="col" class="num">Costs</th></tr></thead><tbody>${invoices}${platform}${oneOff}</tbody></table></div>
            ${notes.map((note) => `<p class="item-note"><strong>Current item note:</strong> ${text(note)}</p>`).join('')}
            ${row.platformCosts.length ? '<button class="btn btn-outline btn-sm" data-manage-costs>Manage platform costs</button>' : ''}`;
    }
}
customElements.define('billing-totals', BillingTotalsComponent);
